import React, { useState, useCallback, useEffect } from 'react';
import { classNames } from '../../../../helpers/classNames';
import BlankModal from '../../../modals/BlankModal';

const AddNewView = ({
  showAddView,
  setShowAddView,
  smartTableId,
  onCreateView,
  isCreating = false,
  sourceView = null // For copying from existing view
}) => {
  const [newViewName, setNewViewName] = useState("");
  const [whoCanEdit, setWhoCanEdit] = useState('personal');

  // Set initial values when copying from a source view
  useEffect(() => {
    if (sourceView && showAddView) {
      setNewViewName(`${sourceView.name} (Copy)`);
      setWhoCanEdit(sourceView.advisor_id ? 'personal' : 'colaborative');
    } else if (showAddView && !sourceView) {
      // Reset when creating new view (not copying)
      setNewViewName("");
      setWhoCanEdit('personal');
    }
  }, [sourceView, showAddView]);

  const handleAddNewView = useCallback((e) => {
    e.preventDefault();

    if (!newViewName.trim()) {
      return;
    }

    // Pass the source view data if copying
    onCreateView(newViewName.trim(), whoCanEdit, sourceView);

    // Reset form
    setNewViewName("");
    setWhoCanEdit("personal");
    setShowAddView(false);
  }, [newViewName, whoCanEdit, onCreateView, sourceView, setShowAddView]);

  const handleCancel = useCallback(() => {
    setShowAddView(false);
    setNewViewName("");
    setWhoCanEdit("personal");
  }, [setShowAddView]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddNewView(e);
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  }, [handleAddNewView, handleCancel]);

  const isCopying = !!sourceView;

  return (
    <BlankModal
      open={showAddView}
      setOpen={setShowAddView}
      title={isCopying ? `Copy View: ${sourceView?.name}` : "Create New View"}
      showCloseButton={true}
    >
      <div className="space-y-4">
        {isCopying && (
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p className="text-sm text-blue-800">
              <strong>Copying view:</strong> This will create a new view with the same filters,
              sorts, column order, and hidden columns as "{sourceView?.name}".
            </p>
          </div>
        )}

        <form onSubmit={handleAddNewView} className="space-y-4">
          <div>
            <label htmlFor="view-name" className="block text-sm font-medium text-gray-700 mb-2">
              View Name
            </label>
            <input
              id="view-name"
              type="text"
              name="New View"
              autoComplete="off"
              className={classNames(
                "w-full px-3 py-2 text-sm border border-gray-300 rounded-md",
                "focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                "disabled:bg-gray-50 disabled:text-gray-500"
              )}
              placeholder="Enter view name..."
              value={newViewName}
              onChange={(e) => setNewViewName(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              disabled={isCreating}
            />
          </div>

          <div>
            <label htmlFor="who-can-edit" className="block text-sm font-medium text-gray-700 mb-2">
              Who can edit
            </label>
            <select
              id="who-can-edit"
              name="who-can-edit"
              className={classNames(
                "w-full px-3 py-2 text-sm border border-gray-300 rounded-md",
                "focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
                "disabled:bg-gray-50 disabled:text-gray-500"
              )}
              value={whoCanEdit}
              onChange={(e) => setWhoCanEdit(e.target.value)}
              disabled={isCreating}
            >
              <option value="personal">Personal</option>
              <option value="colaborative">Collaborative</option>
            </select>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              className={classNames(
                "px-4 py-2 text-sm font-medium rounded-md transition-colors duration-150",
                "border border-gray-300 text-gray-700 bg-white hover:bg-gray-50",
                "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
                isCreating && "opacity-50 cursor-not-allowed"
              )}
              onClick={handleCancel}
              disabled={isCreating}
            >
              Cancel
            </button>

            <button
              type="submit"
              className={classNames(
                "px-4 py-2 text-sm font-medium rounded-md transition-colors duration-150",
                "text-white bg-blue-600 hover:bg-blue-700",
                "focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                (!newViewName.trim() || isCreating) && "opacity-50 cursor-not-allowed"
              )}
              disabled={!newViewName.trim() || isCreating}
            >
              {isCreating ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>{isCopying ? 'Copying...' : 'Creating...'}</span>
                </div>
              ) : (
                isCopying ? 'Copy View' : 'Create View'
              )}
            </button>
          </div>
        </form>
      </div>
    </BlankModal>
  );
};

export default AddNewView;